import React, { useState, useEffect } from 'react';
import { X, Filter, Check, Building2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';
import { Input } from '../ui/Input';

const DepartmentFilterModal = ({ 
  isOpen, 
  onClose, 
  onApply, 
  selectedDepartments = [],
  departments = []
}) => {
  const [localSelected, setLocalSelected] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLocalSelected([...selectedDepartments]);
      setSearchQuery('');
    }
  }, [isOpen, selectedDepartments]);

  if (!isOpen) return null;

  const filteredDepartments = departments.filter(dept =>
    dept.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggle = (deptValue) => {
    setLocalSelected(prev => {
      if (prev.includes(deptValue)) {
        return prev.filter(d => d !== deptValue);
      } else {
        return [...prev, deptValue];
      }
    });
  };

  const handleSelectAll = () => {
    if (localSelected.length === filteredDepartments.length) {
      setLocalSelected([]);
    } else {
      setLocalSelected(filteredDepartments.map(d => d.id || d.name));
    }
  };

  const handleClearAll = () => {
    setLocalSelected([]);
  };

  const handleApply = () => {
    onApply(localSelected);
    onClose();
  };

  const handleReset = () => {
    setLocalSelected([...selectedDepartments]);
  };

  const allSelected = localSelected.length === filteredDepartments.length && filteredDepartments.length > 0;
  const noneSelected = localSelected.length === 0;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Filter by Department
              </h2>
              <p className="text-sm text-gray-500">
                Select departments to filter
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 flex-1 overflow-y-auto">
          {/* Search */}
          <div className="relative">
            <Input
              placeholder="Search departments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            <Filter className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>

          {/* Quick Actions */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-200">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
              className="text-sm"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="text-sm text-red-600 hover:text-red-700"
              disabled={noneSelected}
            >
              Clear All
            </Button>
          </div>

          {/* Department Options */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {filteredDepartments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No departments found</p>
              </div>
            ) : (
              filteredDepartments.map((dept) => {
                const deptValue = dept.id || dept.name;
                const isSelected = localSelected.includes(deptValue);
                
                return (
                  <div
                    key={deptValue}
                    className={`
                      flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all
                      ${isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                    onClick={() => handleToggle(deptValue)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggle(deptValue)}
                        className="pointer-events-none"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{dept.name}</p>
                        {dept.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{dept.description}</p>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <div className="ml-2">
                        <Check className="h-5 w-5 text-blue-600" />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Selected Count */}
          <div className="pt-3 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              {localSelected.length === 0 ? (
                'No departments selected'
              ) : localSelected.length === filteredDepartments.length ? (
                'All departments selected'
              ) : (
                `${localSelected.length} of ${filteredDepartments.length} departments selected`
              )}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={JSON.stringify(localSelected) === JSON.stringify(selectedDepartments)}
          >
            Reset
          </Button>
          <Button
            onClick={handleApply}
            disabled={localSelected.length === 0}
          >
            Apply Filter
            {localSelected.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                {localSelected.length}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DepartmentFilterModal;

